import React, { useState, useEffect } from "react";
import CustomModal from "../../components/CustomModal";
import { useCreateWebinar, useUpdateWebinar } from "./hooks";
import {
  Webinar,
  WebinarSource,
  WebinarStatus,
  WebinarResource,
  getThumbnailUrl,
  webinarsAPI,
} from "./api";
import { useT } from "../../i18n";

interface NewWebinarModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  editWebinar?: Webinar | null;
}

const sources: { value: WebinarSource; icon: string }[] = [
  { value: "youtube", icon: "fa-brands fa-youtube" },
  { value: "vimeo", icon: "fa-brands fa-vimeo-v" },
  { value: "zoom", icon: "fa-solid fa-video" },
  { value: "iframe", icon: "fa-solid fa-link" },
];

const NewWebinarModal: React.FC<NewWebinarModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  editWebinar,
}) => {
  const t = useT();
  const [title, setTitle] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [source, setSource] = useState<WebinarSource>("youtube");
  const [description, setDescription] = useState("");
  const [resources, setResources] = useState<WebinarResource[]>([]);
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const [error, setError] = useState("");
  // Add Link modal draft fields
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkTitle, setLinkTitle] = useState("");
  const [linkUrl, setLinkUrl] = useState("");

  const createWebinarMutation = useCreateWebinar();
  const updateWebinarMutation = useUpdateWebinar();

  const isEditMode = !!editWebinar;

  useEffect(() => {
    if (isEditMode && editWebinar) {
      setTitle(editWebinar.title);
      setVideoUrl(editWebinar.videoUrl);
      setSource(editWebinar.source);
      setDescription(editWebinar.description || "");
      setResources(editWebinar.resources || []);
    } else if (!isEditMode) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editWebinar, isEditMode, isOpen]);

  const resetForm = () => {
    setTitle("");
    setVideoUrl("");
    setSource("youtube");
    setDescription("");
    setResources([]);
    setUploadingIndex(null);
    setError("");
    setLinkModalOpen(false);
    setLinkTitle("");
    setLinkUrl("");
  };

  // --- Resource list helpers ---
  const removeResource = (index: number) => {
    setResources((prev) => prev.filter((_, i) => i !== index));
  };

  // Add Link via modal.
  const openLinkModal = () => {
    setLinkTitle("");
    setLinkUrl("");
    setLinkModalOpen(true);
  };
  const saveLink = () => {
    if (!linkTitle.trim() || !linkUrl.trim()) return;
    setResources((prev) => [
      ...prev,
      { title: linkTitle.trim(), url: linkUrl.trim(), type: "link" },
    ]);
    setLinkModalOpen(false);
  };

  // Add File directly: create a slot then upload into it.
  const addFileDirect = async (file: File) => {
    setError("");
    const index = resources.length;
    setResources((prev) => [
      ...prev,
      { title: file.name, url: "", type: "file" },
    ]);
    setUploadingIndex(index);
    try {
      const res = await webinarsAPI.uploadResourceFile(file, title);
      setResources((prev) =>
        prev.map((r, i) =>
          i === index
            ? {
                ...r,
                url: res.fileUrl,
                fileKey: res.fileKey,
                title: res.fileName,
                fileType: res.fileType,
                fileSize: res.fileSize,
              }
            : r
        )
      );
    } catch (err: any) {
      setError(t("webinars.modal.errors.uploadFailed"));
      // Roll back the empty slot on failure.
      setResources((prev) => prev.filter((_, i) => i !== index));
    } finally {
      setUploadingIndex(null);
    }
  };

  const handleSave = async () => {
    setError("");
    if (!title || !videoUrl || !source) {
      setError(t("common.validation.requiredFields"));
      return;
    }
    try {
      // Keep only resources that have both a title and a URL.
      const cleanResources = resources.filter(
        (r) => r.title.trim() && r.url.trim()
      );
      const payload = {
        title,
        videoUrl,
        source,
        status: "recorded" as WebinarStatus,
        description: description || undefined,
        resources: cleanResources,
      };
      if (isEditMode && editWebinar) {
        await updateWebinarMutation.mutateAsync({
          id: editWebinar.id,
          data: payload,
        });
      } else {
        await createWebinarMutation.mutateAsync(payload);
      }
      resetForm();
      onClose();
      onSuccess?.();
    } catch (err: any) {
      setError(t("webinars.modal.errors.saveFailed"));
    }
  };

  const isSaving =
    createWebinarMutation.isPending || updateWebinarMutation.isPending;

  const thumb = getThumbnailUrl(videoUrl, source);

  const inputClass =
    "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition";

  return (
    <CustomModal open={isOpen} onClose={onClose}>
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-red-50 flex items-center justify-center">
              <i className="fa-solid fa-video text-red-600"></i>
            </div>
            <h2 className="text-lg font-bold text-gray-900">
              {isEditMode ? t("webinars.modal.editTitle") : t("webinars.modal.createTitle")}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="h-8 w-8 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 flex items-center justify-center"
          >
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-center gap-2">
              <i className="fa-solid fa-circle-exclamation"></i>
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("webinars.modal.titleLabel")} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={inputClass}
                placeholder={t("webinars.modal.titlePlaceholder")}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("webinars.modal.videoUrlLabel")} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                className={inputClass}
                placeholder={t("webinars.modal.videoUrlPlaceholder")}
              />
              {thumb && (
                <div className="mt-2 rounded-lg overflow-hidden border border-gray-200 w-40 aspect-video">
                  <img
                    src={thumb}
                    alt={t("common.preview")}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>

            {/* Source segmented control */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {t("webinars.modal.sourceLabel")} <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {sources.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => setSource(s.value)}
                    className={`flex flex-col items-center gap-1 py-2 rounded-lg border text-xs font-medium transition ${
                      source === s.value
                        ? "border-red-500 bg-red-50 text-red-700"
                        : "border-gray-200 text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <i className={`${s.icon} text-base`}></i>
                    {t(`webinars.sources.${s.value}`)}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("webinars.modal.descriptionLabel")}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className={inputClass}
                placeholder={t("webinars.modal.descriptionPlaceholder")}
              />
            </div>

            {/* Resources */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium text-gray-700">
                  {t("webinars.modal.resourcesLabel")}
                </label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={openLinkModal}
                    className="text-xs font-medium text-red-600 hover:text-red-700 flex items-center gap-1"
                  >
                    <i className="fa-solid fa-link"></i> {t("webinars.modal.addLink")}
                  </button>
                  <label className="text-xs font-medium text-red-600 hover:text-red-700 flex items-center gap-1 cursor-pointer">
                    <i className="fa-solid fa-upload"></i> {t("webinars.modal.addFile")}
                    <input
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) addFileDirect(f);
                        e.target.value = "";
                      }}
                    />
                  </label>
                </div>
              </div>

              {resources.length === 0 && (
                <p className="text-xs text-gray-400">
                  {t("webinars.modal.resourcesHint")}
                </p>
              )}

              <div className="space-y-2">
                {resources.map((r, i) => {
                  const isUploading = uploadingIndex === i;
                  return (
                    <div
                      key={i}
                      className="flex items-center justify-between gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-gray-50/50"
                    >
                      <span className="flex items-center gap-2 min-w-0 text-sm text-gray-700">
                        {isUploading ? (
                          <i className="fa-solid fa-spinner fa-spin text-gray-400"></i>
                        ) : (
                          <i
                            className={`fa-solid ${
                              r.type === "file" ? "fa-file-lines" : "fa-link"
                            } text-gray-400`}
                          ></i>
                        )}
                        <span className="truncate">{r.title || r.url}</span>
                        {r.type === "file" && r.fileSize && (
                          <span className="text-xs text-gray-400 shrink-0">
                            {r.fileType} • {r.fileSize}
                          </span>
                        )}
                      </span>
                      <div className="flex items-center gap-3 shrink-0">
                        {r.url && (
                          <a
                            href={r.url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-red-600 font-medium"
                          >
                            {t("webinars.modal.view")}
                          </a>
                        )}
                        <button
                          type="button"
                          onClick={() => removeResource(i)}
                          className="text-xs text-gray-400 hover:text-red-600"
                          title={t("webinars.modal.remove")}
                        >
                          <i className="fa-solid fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
            >
              {t("common.cancel")}
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-5 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-60 transition flex items-center gap-2"
            >
              {isSaving && (
                <i className="fa-solid fa-spinner fa-spin"></i>
              )}
              {isSaving
                ? t("common.loading.saving")
                : isEditMode
                ? t("common.update")
                : t("common.create")}
            </button>
          </div>
        </div>
      </div>

      {/* Add Link modal */}
      {linkModalOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
          onClick={() => setLinkModalOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <i className="fa-solid fa-link text-red-600"></i>{" "}
                {t("webinars.modal.linkModalTitle")}
              </h3>
              <button
                onClick={() => setLinkModalOpen(false)}
                className="h-8 w-8 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 flex items-center justify-center"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("webinars.modal.linkTitleLabel")} <span className="text-red-500">*</span>
                </label>
                <input
                  autoFocus
                  value={linkTitle}
                  onChange={(e) => setLinkTitle(e.target.value)}
                  className={inputClass}
                  placeholder={t("webinars.modal.linkTitlePlaceholder")}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("webinars.modal.linkUrlLabel")} <span className="text-red-500">*</span>
                </label>
                <input
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && saveLink()}
                  className={inputClass}
                  placeholder="https://..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button
                onClick={() => setLinkModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={saveLink}
                disabled={!linkTitle.trim() || !linkUrl.trim()}
                className="px-5 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {t("webinars.modal.addLinkSubmit")}
              </button>
            </div>
          </div>
        </div>
      )}
    </CustomModal>
  );
};

export default NewWebinarModal;
